import React, {useContext, useEffect, useState} from "react";

// Services
import {NotificationsContext} from "../../../ui-components/Notifications";
import apiRequest from "../../../services/apiRequest";
import CountryContext from "../../../CountryContext";
import {FormattedMessage, useIntl} from "react-intl";

// Components
import Placeholder from "../../../ui-components/Placeholder";
import {PageSection} from "../../../ui-components/Container";
import Select from "../../../ui-components/Select";
import UsersTrendChart from "./UsersTrendChart";
import UsersHeatMap from "./UsersHeatMap";


import {getRoleOptions, getStatusOptions, getTrendChart} from "./Users.lib";
import {AllUsersKpi, GroupUsersKpi} from "./Users.lib";

// Types
import {
  AllUsers,
  AnalyticsUsersProps,
  FilterRole,
  FilterDetail,
  Users,
  GeoData,
  TrendData,
  DetailData
} from "./Users.type";
import UsersKpiDetailTable from "../../../components/Tables/UsersKpiDetailTable";
import _ from "lodash";


export function AnalyticsUsers(
  {
    visibilityLevel,
    visibilityUserId,
    analysisPeriod
  }: AnalyticsUsersProps
) {

  const intl = useIntl();
  const {country} = useContext(CountryContext);
  const {push} = useContext(NotificationsContext);


  const [allUsers, setAllUsers] = useState<AllUsers | undefined>(undefined)
  const [internalUsers, setInternalUsers] = useState<Users | undefined>(undefined)
  const [externalUsers, setExternalUsers] = useState<Users | undefined>(undefined)

  const [filterRole, setFilterRole] = useState<FilterRole>({internal: '', external: ''});
  const [filterDetail, setFilterDetail] = useState<FilterDetail>({role: 'all', status: 'registrati'});

  const [geo, setGeo] = useState<GeoData[] | undefined>(undefined)
  const [trend, setTrend] = useState<TrendData[] | undefined>(undefined)
  const [detail, setDetail] = useState<DetailData[] | undefined>(undefined)
  const [sales, setSales] = useState<any | undefined>(undefined)

  useEffect(() => {

    Promise.all([apiRequest.get("/users/internal")])
      .then((res) => {
        setSales(
          // @ts-ignore
          (res[0] || []).filter((u) => u.role.name === "agent" && u.id_salesforce)
            // @ts-ignore
            .sort((a, b) => a.lastname.localeCompare(b.lastname))
            // @ts-ignore
            .reduce((obj, i) => _.set(obj, [i.id_salesforce], `${i.lastname} ${i.firstname}`.toUpperCase()), {})
        );
      })
      .catch(() => {push({title: "server_error", type: "error"})});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = {
    country: country.code,
    start_date: analysisPeriod.start,
    end_date: analysisPeriod.end,
    visibility_user_id: visibilityUserId
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [all] = await Promise.all([
          // @ts-ignore
          apiRequest.post("/analytics/users/status", payload)
        ]);
        // @ts-ignore
        setAllUsers(all)
      } catch (error) {
        console.log(error)
        setAllUsers({data: [], roles: []})
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
      }
    };

    fetchData()

  }, [visibilityUserId, analysisPeriod]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [internal] = await Promise.all([
          // @ts-ignore
          apiRequest.post("/analytics/users/status",
            {...payload, flg_internal: true, role: filterRole.internal})
        ]);
        // @ts-ignore
        setInternalUsers(internal)

      } catch (error) {
        console.log(error)
        setInternalUsers({data: []})
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
      }
    };

    fetchData()

  }, [visibilityUserId, analysisPeriod, filterRole.internal]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [external] = await Promise.all([
          // @ts-ignore
          apiRequest.post("/analytics/users/status",
            {...payload, flg_internal: false, role: filterRole.external})
        ]);
        // @ts-ignore
        setExternalUsers(external)

      } catch (error) {
        console.log(error)
        setExternalUsers({data: []})
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
      }
    };

    fetchData()

  }, [visibilityUserId, analysisPeriod, filterRole.external]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        country: country.code,
        visibility_user_id: visibilityUserId,
        role: filterDetail.role,
        status: filterDetail.status,
        start_date: analysisPeriod.start,
        end_date: analysisPeriod.end,
      }
      try {
        const [geo, trend, detail] = await Promise.all([
          // @ts-ignore
          apiRequest.post("/analytics/users/geo", {...payload}),
          // @ts-ignore
          apiRequest.post("/analytics/users/trend", {...payload}),
          // @ts-ignore
          apiRequest.post("/analytics/users/detail", {...payload})
        ]);
        // @ts-ignore
        setGeo(geo)
        // @ts-ignore
        setTrend(trend)
        // @ts-ignore
        setDetail(detail)

      } catch (error) {
        console.log(error)
        setGeo([])
        setTrend([])
        setDetail([])
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
      }
    };

    fetchData()

  }, [visibilityUserId, filterDetail]) // eslint-disable-line react-hooks/exhaustive-deps

  const getOptions = (i: 'internal' | 'external') => {
    let options = [{value: '', name: 'All'}]
    if (allUsers?.roles.length) {
      const filtered = allUsers.roles.filter(r => i === 'internal' ? r.flg_internal : !r.flg_internal)
      options = options.concat(filtered.map(i => ({value: i.name, name: intl.formatMessage({id: i.name})})))
    }
    return options
  }

  return <div className="w-full">
    <div>
      <dl className="mb-6 grid grid-cols-1 overflow-hidden bg-white shadow xl:grid-cols-2">
        {visibilityLevel < 4 ?
          <>
            {allUsers ?
              <AllUsersKpi allUsers={allUsers}/>
              :
              <div className="px-4 py-5 xl:p-6 xl:col-span-2 border">
                <Placeholder height="h-32"/>
              </div>
            }
          </> : null
        }
        {['internal', 'external'].map((i) => {
          const data = i === 'internal' ? internalUsers : externalUsers
          const colUi = i === 'external' && visibilityLevel > 3 ? 'xl:col-span-2' : ''
          return (
            i === 'external' || visibilityLevel < 4 ?
              <div key={i} className={`px-4 py-5 xl:p-6 border border-t-0 ${colUi}`}>
                {data && allUsers ?
                  <GroupUsersKpi
                    name={i as 'internal' | 'external'}
                    users={data}
                    filterRole={filterRole}
                    setFilterRole={setFilterRole}
                    options={getOptions(i as 'internal' | 'external')}
                  /> :
                  <div className="px-4 py-5 xl:p-6">
                    <Placeholder height="h-32"/>
                  </div>
                }
              </div> : null
          )
        })}
      </dl>
    </div>
    <PageSection
      title={<span className="font-bold"><FormattedMessage id="detail" defaultMessage="Dettaglio" /></span>}
      description={undefined}
    >
      {allUsers && allUsers.roles ?
        <div>
          <div className="w-full flex flex-col lg:flex-row my-4">
            <div className={`w-full ${country.code === "it" ? "md:w-2/3" : ""} flex flex-col`}>
              <div className="w-full flex flex-col sm:flex-row items-center sm:gap-10 justify-center">
                <Select
                  label={intl.formatMessage({id: 'filter_by_role'})}
                  value={filterDetail.role}
                  options={getRoleOptions(allUsers.roles, visibilityLevel, intl)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDetail({
                    ...filterDetail,
                    role: e.target.value
                  })}
                  className={undefined}
                />
                <Select
                  label={intl.formatMessage({id: 'filter_by_status'})}
                  value={filterDetail.status}
                  options={getStatusOptions()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDetail({
                    ...filterDetail,
                    status: e.target.value
                  })}
                  className={undefined}
                />
              </div>
              {trend ?
                <UsersTrendChart
                  id={'UsersTrendChart'}
                  data={getTrendChart(trend)}
                />
                : <div className="p-8">
                  <Placeholder height="h-56"/>
                </div>
              }
            </div>
            {
              country.code === "it"
                ? (
                  <div className="w-full md:w-1/3">
                    {geo ?
                      <div className="w-full text-center flex flex-col gap-y-5">
                        <span className="font-medium">{intl.formatMessage({id: "external_users_map"})}</span>
                        <UsersHeatMap
                          id="UsersHeatMap"
                          data={geo.map(i => ({value: i.val_users, id: 'IT-' + i.cod_province}))}
                        />
                      </div>
                      : <div className="p-8">
                        <Placeholder height="h-72"/>
                      </div>
                    }
                  </div>
                )
                : null
            }
          </div>
          {sales && detail ?
            <div className="w-full mb-4">
              <UsersKpiDetailTable
                intl={intl}
                sales={sales}
                countryCode={country.code}
                detail={detail}
                isManager={visibilityLevel < 4}
              />
            </div>
            : <div className="p-8">
              <Placeholder height="h-72"/>
            </div>
          }
        </div> : null
      }
    </PageSection>
  </div>
}